import { PASSWORD_MIN_LENGTH as MIN_LENGTH } from '@remote-medium/shared';

const COMMON = ['password', 'qwerty', 'letmein', 'welcome', 'admin', 'iloveyou', '123456'];

const LEVELS = [
  { label: 'Too weak', bar: 'bg-destructive', text: 'text-destructive' },
  { label: 'Weak', bar: 'bg-destructive', text: 'text-destructive' },
  { label: 'Fair', bar: 'bg-warning', text: 'text-warning' },
  { label: 'Good', bar: 'bg-success', text: 'text-success' },
  { label: 'Strong', bar: 'bg-success', text: 'text-success' },
] as const;

function isCommon(password: string): boolean {
  const lower = password.toLowerCase();
  // A passphrase that merely contains a common word is fine; flag passwords
  // that are essentially that word plus a few extra characters.
  return COMMON.some((w) => lower.includes(w) && password.length - w.length <= 6);
}

function score(password: string): number {
  if (password.length === 0) return 0;
  if (isCommon(password)) return password.length >= MIN_LENGTH ? 1 : 0;
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(password)).length;
  const unique = new Set(password).size;
  let s = 0;
  if (password.length >= MIN_LENGTH) s += 1;
  if (password.length >= 16) s += 1;
  if (classes >= 3) s += 1;
  if (unique >= 8 && !/(.)\1{3,}/.test(password)) s += 1;
  // Below the enforced minimum it can never read as acceptable.
  return password.length < MIN_LENGTH ? Math.min(s, 1) : s;
}

export function PasswordStrength({ password }: { password: string }) {
  if (password.length === 0) return null;
  const s = score(password);
  const level = LEVELS[s]!;
  const checks = [
    { ok: password.length >= MIN_LENGTH, label: `At least ${MIN_LENGTH} characters (required)` },
    {
      ok: /[a-z]/.test(password) && /[A-Z]/.test(password),
      label: 'Upper and lower case (suggested)',
    },
    { ok: /\d/.test(password), label: 'A number (suggested)' },
    { ok: /[^A-Za-z0-9]/.test(password), label: 'A symbol (suggested)' },
  ];
  return (
    <div className="grid gap-2" aria-live="polite">
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1" aria-hidden="true">
          {[1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full ${i <= s ? level.bar : 'bg-muted'}`}
            />
          ))}
        </div>
        <span className={`text-xs font-medium ${level.text}`}>{level.label}</span>
      </div>
      <ul className="m-0 grid list-none gap-1 p-0 text-xs text-muted-foreground">
        {checks.map((c) => (
          <li key={c.label} className={c.ok ? 'text-success' : undefined}>
            {c.ok ? '✓' : '○'} {c.label}
          </li>
        ))}
      </ul>
      {isCommon(password) && (
        <p className="m-0 text-xs text-warning">
          Contains a common word or sequence (like &ldquo;password&rdquo; or &ldquo;123456&rdquo;),
          which is easy to guess. Try a few unrelated words instead.
        </p>
      )}
    </div>
  );
}
